import { useCallback, useState } from 'react';
import { format } from 'date-fns';
import type { FeeVoucherPrintSet } from '../../types/fees';
import { usePdfExport, type SchoolBranding } from '../../utils/pdf';
import FeeVoucherDocument from './pdf/FeeVoucherDocument';

/**
 * Fee vouchers drawn in the browser — one challan or a pack. Each download
 * is keyed (a challan's id, "pack") so its own button can show it is busy;
 * `packing` is how many vouchers a pack in progress holds, for the progress
 * dialog. Staff pass the school's branding; the parent's status page its own.
 */
export function useFeeVoucherPdf(branding: SchoolBranding) {
  const { generate } = usePdfExport();
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [packing, setPacking] = useState(0);

  const toggle = (key: string, on: boolean) => setBusy(prev => {
    const next = new Set(prev);
    if (on) next.add(key); else next.delete(key);
    return next;
  });

  /** Draws the vouchers `load` returns. `count`, when known up front, shows the progress dialog. Resolves true once saved. */
  const download = useCallback(async (key: string, load: () => Promise<FeeVoucherPrintSet>, count?: number): Promise<boolean> => {
    toggle(key, true);
    if (count) setPacking(count);
    let names: string[] = [];
    try {
      return await generate(async () => {
        const set = await load();
        names = set.vouchers.map(v => v.invoiceNumber);
        return <FeeVoucherDocument set={set} branding={branding} generatedAt={new Date().toISOString()} />;
      }, () => (names.length === 1
        ? `Fee-Voucher-${names[0]}.pdf`
        : `Fee-Vouchers-${names.length}-${format(new Date(), 'yyyy-MM-dd')}.pdf`));
    } finally {
      toggle(key, false);
      if (count) setPacking(0);
    }
  }, [generate, branding]);

  return { download, isBusy: (key: string) => busy.has(key), packing };
}
